import React, { useEffect, useMemo, useState } from 'react';
import { Gift, RefreshCw } from 'lucide-react';
import { database, ref, get } from '../../firebase';
import { fetchAllScratchHistory, fetchScratchMinRewards, saveScratchMinRewards } from '../../utils/scratchCardService';

const formatAmount = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const formatDateTime = (value, fallbackDate) => {
  const d = new Date(value || fallbackDate || '');
  if (Number.isNaN(d.getTime())) return fallbackDate || 'N/A';
  return d.toLocaleString();
};

const fetchLegacyAdminScratchEntries = async () => {
  const snap = await get(ref(database, 'UserFundTransactions'));
  if (!snap.exists()) return [];

  const rows = [];
  const data = snap.val();

  Object.entries(data).forEach(([dateKey, users]) => {
    if (!users || typeof users !== 'object') return;
    Object.entries(users).forEach(([userKey, txs]) => {
      if (!txs || typeof txs !== 'object') return;
      Object.entries(txs).forEach(([id, tx]) => {
        if (!tx || tx.type !== 'scratch_reward') return;
        rows.push({
          id,
          dateKey,
          userKey,
          userId: tx.userId || tx.memberId || tx.sponsorId || userKey,
          userName: tx.userName || '',
          amount: Number(tx.amount) || 0,
          eventType: String(tx.description || '').toLowerCase().includes('package') ? 'package_activation' : 'fund_add',
          description: tx.description || 'Scratch reward',
          referenceId: tx.referenceId || '',
          timestamp: tx.timestamp,
          timestampFormatted: tx.timestampFormatted,
          source: 'UserFundTransactions'
        });
      });
    });
  });

  return rows;
};

const Scratch = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fundMinInput, setFundMinInput] = useState('1');
  const [packageMinInput, setPackageMinInput] = useState('2');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [newScratchList, legacyList, minSettings] = await Promise.all([
        fetchAllScratchHistory(),
        fetchLegacyAdminScratchEntries(),
        fetchScratchMinRewards()
      ]);

      const merged = [...newScratchList];
      legacyList.forEach((item) => {
        const exists = merged.some(
          (row) =>
            String(row.referenceId || '') === String(item.referenceId || '') &&
            String(row.eventType || '') === String(item.eventType || '') &&
            Number(row.amount || 0) === Number(item.amount || 0) &&
            String(row.userId || row.memberId || row.sponsorId || row.userKey || '') ===
              String(item.userId || item.memberId || item.sponsorId || item.userKey || '')
        );
        if (!exists) merged.push(item);
      });

      merged.sort((a, b) => {
        const tA = new Date(a.timestampFormatted || a.timestamp || 0).getTime() || 0;
        const tB = new Date(b.timestampFormatted || b.timestamp || 0).getTime() || 0;
        return tB - tA;
      });

      setRows(merged);
      setFundMinInput(String(Number(minSettings.fund_add) || 1));
      setPackageMinInput(String(Number(minSettings.package_activation) || 2));
    } catch (error) {
      console.error('Error fetching admin scratch history:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMinimums = async () => {
    setSettingsMessage('');
    setSavingSettings(true);
    try {
      const fundMin = Number(fundMinInput);
      const packageMin = Number(packageMinInput);
      const result = await saveScratchMinRewards({
        fund_add: fundMin,
        package_activation: packageMin
      });
      if (!result.success) {
        setSettingsMessage(result.error || 'Unable to save minimum settings');
      } else {
        setSettingsMessage('Minimum settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving scratch minimum settings:', error);
      setSettingsMessage('Failed to save minimum settings');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totalAmount = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [rows]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <Gift size={22} className="text-yellow-400" />
            Scratch Card Report
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Total Scratch Rewards: <span className="text-green-400 font-semibold">{formatAmount(totalAmount)}</span> | Entries: <span className="text-white">{rows.length}</span>
          </p>
        </div>
        <button
          onClick={loadData}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 rounded-lg border border-border bg-secondary/20">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Fund Scratch Minimum ($)</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={fundMinInput}
            onChange={(e) => setFundMinInput(e.target.value)}
            className="w-full px-3 py-2 bg-[#0f172a] border border-gray-600 rounded-lg text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Package Scratch Minimum ($)</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={packageMinInput}
            onChange={(e) => setPackageMinInput(e.target.value)}
            className="w-full px-3 py-2 bg-[#0f172a] border border-gray-600 rounded-lg text-white"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={handleSaveMinimums}
            disabled={savingSettings}
            className="w-full px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-60"
          >
            {savingSettings ? 'Saving...' : 'Add / Save Minimum'}
          </button>
        </div>
      </div>
      {settingsMessage && <div className="text-sm text-cyan-300">{settingsMessage}</div>}

      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading scratch report...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-gray-400">No scratch data found under `scratch`</div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-300">Date</th>
                  <th className="text-left px-4 py-3 text-gray-300">User ID</th>
                  <th className="text-left px-4 py-3 text-gray-300">Name</th>
                  <th className="text-left px-4 py-3 text-gray-300">Type</th>
                  <th className="text-left px-4 py-3 text-gray-300">Reference</th>
                  <th className="text-right px-4 py-3 text-gray-300">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={`${item.dateKey}-${item.id}`} className="border-t border-border">
                    <td className="px-4 py-3 text-white">{formatDateTime(item.timestampFormatted || item.timestamp, item.dateKey)}</td>
                    <td className="px-4 py-3 text-cyan-300">{item.userId || item.memberId || item.sponsorId || item.userKey || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-200">{item.userName || '-'}</td>
                    <td className="px-4 py-3 text-gray-300">{item.eventType || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-400">{item.referenceId || '-'}</td>
                    <td className="px-4 py-3 text-right text-green-400 font-semibold">{formatAmount(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {rows.map((item) => (
              <div key={`${item.dateKey}-${item.id}`} className="rounded-lg border border-border p-3 bg-secondary/20">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400">{formatDateTime(item.timestampFormatted || item.timestamp, item.dateKey)}</div>
                  <div className="text-green-400 font-bold">{formatAmount(item.amount)}</div>
                </div>
                <div className="text-sm text-cyan-300 mt-2">
                  {item.userId || item.memberId || item.sponsorId || item.userKey || 'N/A'}
                </div>
                <div className="text-sm text-gray-300">{item.userName || '-'}</div>
                <div className="text-xs text-gray-400 mt-1">Type: {item.eventType || 'N/A'}</div>
                <div className="text-xs text-gray-400">Ref: {item.referenceId || '-'}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Scratch;
